import { useCallback } from 'react';
import { css } from '@emotion/react';
import { AuthMethod, Contact } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { UsersMutations } from '@frontend/api-users';
import {
  useMFAContext,
  MFAFormStep,
  SelectMFAMethod,
  AddMFAAddress,
  VerifyMFAAddress,
  AddMfaMethodButton,
} from '@frontend/account';
import { navigateToUrl } from '@frontend/auth';
import { getUser } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { MFAPromptTitle } from '../../components/mfa/mfa-prompt-title';
import { SkipButton } from '../../components/mfa/skip-button';

type MFAPromptFlowProps = {
  redirectTo?: string;
  mfaMethods?: Contact[];
};

/**
 * The flow for the post-login MFA prompt. Resembles the MFA flow in the settings page (libs/features/account/src/components/users-2.0/mfa/mfa-form.tsx)
 */
export function MFAPromptFlow({ redirectTo = appConfig.DEFAULT_REDIRECT, mfaMethods }: MFAPromptFlowProps) {
  const { t } = useTranslation('account');
  const step = useMFAContext((ctx) => ctx.step);

  const { mutateAsync: enableMFAMutation, isLoading } = UsersMutations.useEnableMFA();

  // enables MFA and navigates to the redirect URL
  const onVerifySuccess = useCallback(async () => {
    const userID = getUser()?.userID;
    // enabling MFA is a separate step from adding a new method
    // so we call this once they've verified the new method
    if (userID) {
      await enableMFAMutation({ userId: userID });
    }
    // top-level navigation to the new app
    navigateToUrl(redirectTo);
  }, [enableMFAMutation, redirectTo]);

  const hasMFAMethods = mfaMethods && mfaMethods.length > 0;
  const defaultMethod = hasMFAMethods && mfaMethods[0];

  return (
    <section
      css={css`
        max-width: 450px;
        width: 100%;
      `}
    >
      <MFAPromptTitle />

      <MFAFormStep currentStep={step} step='initial'>
        <Text>
          {t(
            "Help protect your patients' sensitive information by enabling an extra security step. You'll get a code by email or text to ensure only authorized access."
          )}
        </Text>
        {hasMFAMethods && defaultMethod && (
          <AddMfaMethodButton
            icon={defaultMethod.type === AuthMethod.EMAIL ? 'email' : 'message'}
            label={t('Enable using {{contact}}', { contact: defaultMethod.contact })}
            onClick={onVerifySuccess}
            disabled={isLoading}
            css={css`
              margin-bottom: ${theme.spacing(2)};
            `}
            id='use-default-mfa-method-button'
          />
        )}
        <SelectMFAMethod hasMFAMethods={hasMFAMethods} />
        <SkipButton id='mfa-prompt-skip-button' onSkip={() => navigateToUrl(redirectTo)} />
      </MFAFormStep>

      <MFAFormStep currentStep={step} step='add'>
        <AddMFAAddress />
      </MFAFormStep>

      <MFAFormStep currentStep={step} step='verify'>
        <VerifyMFAAddress onVerifySuccess={onVerifySuccess} />
      </MFAFormStep>
    </section>
  );
}

// Uncomment and add in the steps above for testing the UI
// function CheatButton() {
//   const nextStep = useMFAContext((ctx) => ctx.nextStep);
//   return <button onClick={nextStep}>Next Step</button>;
// }
